import React from 'react'
import { Trade, Token } from '@uniswap/sdk'
import styled from 'styled-components'
import { AlertTriangle, AlertOctagon } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { WrappedTokenInfo } from '../../state/lists/hooks'
import { isWarningToken, isSpamToken, getRiskLevelText, getRiskLevelDescription } from '../../constants/tokenRiskLevel'
import { isTaxToken, getTotalBuyTax, getTotalSellTax } from '../../utils/dynamicTaxToken'

const WarningContainer = styled.div<{ severity: 'warning' | 'danger' }>`
  background-color: ${({ theme, severity }) => (severity === 'danger' ? theme.red1 + '15' : theme.yellow2 + '15')};
  border: 1px solid ${({ theme, severity }) => (severity === 'danger' ? theme.red1 : theme.yellow2)};
  border-radius: 12px;
  padding: 12px;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const IconWrapper = styled.div<{ severity: 'warning' | 'danger' }>`
  color: ${({ theme, severity }) => (severity === 'danger' ? theme.red1 : theme.yellow2)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const Title = styled.div<{ severity: 'warning' | 'danger' }>`
  color: ${({ theme, severity }) => (severity === 'danger' ? theme.red1 : theme.yellow2)};
  font-weight: 600;
  font-size: 14px;
`

const Description = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 13px;
  line-height: 1.4;
`

const TokenInfo = styled.div`
  color: ${({ theme }) => theme.text3};
  font-size: 12px;
  margin-top: 4px;
`

const TaxNote = styled.div`
  color: #565a69;
  font-size: 12px;
  margin-top: 8px;
  line-height: 1.5;
  text-align: left;
  padding: 0.2rem 0.75rem 0 0.75rem;
  position: relative;
  top: 0.5rem;
`

const Container = styled.div`
  width: 100%;
`

interface TokenRiskWarningProps {
  trade: Trade | undefined
}

export default function TokenRiskWarning({ trade }: TokenRiskWarningProps) {
  const { t } = useTranslation()

  if (!trade) return null

  const inputToken = trade.inputAmount.currency
  const outputToken = trade.outputAmount.currency

  // 检查输入和输出代币的风险等级
  const inputRiskLevel = inputToken instanceof WrappedTokenInfo ? inputToken.riskLevel : undefined
  const outputRiskLevel = outputToken instanceof WrappedTokenInfo ? outputToken.riskLevel : undefined

  // 判断风险类型
  const hasWarning = isWarningToken(inputRiskLevel) || isWarningToken(outputRiskLevel)
  const hasSpam = isSpamToken(inputRiskLevel) || isSpamToken(outputRiskLevel)

  // 检查税费代币
  const inputTokenAsToken = inputToken instanceof Token ? inputToken : undefined
  const outputTokenAsToken = outputToken instanceof Token ? outputToken : undefined
  const inputIsTaxToken = inputTokenAsToken && isTaxToken(inputTokenAsToken)
  const outputIsTaxToken = outputTokenAsToken && isTaxToken(outputTokenAsToken)
  const hasTaxToken = inputIsTaxToken || outputIsTaxToken

  // 如果没有风险也没有税费，不显示警告
  if (!hasWarning && !hasSpam && !hasTaxToken) return null

  // 优先显示更严重的风险
  const severity = hasSpam ? 'danger' : 'warning'

  // 收集有风险的代币信息
  const riskyTokens: Array<{ symbol: string; riskLevel: number }> = []

  if (inputRiskLevel && (isWarningToken(inputRiskLevel) || isSpamToken(inputRiskLevel))) {
    riskyTokens.push({ symbol: inputToken.symbol || 'Unknown', riskLevel: inputRiskLevel })
  }

  if (outputRiskLevel && (isWarningToken(outputRiskLevel) || isSpamToken(outputRiskLevel))) {
    riskyTokens.push({ symbol: outputToken.symbol || 'Unknown', riskLevel: outputRiskLevel })
  }

  // 获取最高风险等级的描述
  const highestRiskLevel = Math.max(inputRiskLevel || 0, outputRiskLevel || 0)
  const riskText = getRiskLevelText(highestRiskLevel, t)
  const riskDescription = getRiskLevelDescription(highestRiskLevel, t)

  // 计算税费百分比
  const inputTaxPercent = inputIsTaxToken && inputTokenAsToken ? getTotalSellTax(inputTokenAsToken).toFixed(2) : null
  const outputTaxPercent = outputIsTaxToken && outputTokenAsToken ? getTotalBuyTax(outputTokenAsToken).toFixed(2) : null

  // 如果只有税费没有风险警告，只显示税费信息文本
  if (!hasWarning && !hasSpam && hasTaxToken) {
    return (
      <Container>
        <TaxNote>
          {inputIsTaxToken && (
            <>
              {t('sellToken')} {inputTokenAsToken?.symbol} {t('willChargeTax')} {inputTaxPercent}{t('percentTax')}，
            </>
          )}
          {outputIsTaxToken && (
            <>
              {t('buyToken')} {outputTokenAsToken?.symbol} {t('willChargeTax')} {outputTaxPercent}{t('percentTax')}，
            </>
          )}
          {t('taxAutoDeducted')}
        </TaxNote>
      </Container>
    )
  }

  // 如果有风险警告，将税费信息附加在下面
  return (
    <Container>
      <WarningContainer severity={severity}>
        <IconWrapper severity={severity}>
          {severity === 'danger' ? <AlertOctagon size={20} /> : <AlertTriangle size={20} />}
        </IconWrapper>
        <ContentWrapper>
          <Title severity={severity}>{riskText} {t('riskWarning')}</Title>
          <Description>{riskDescription}</Description>
          {riskyTokens.length > 0 && (
            <TokenInfo>
              {t('involvedTokens')}{riskyTokens.map(token => `${token.symbol} (${getRiskLevelText(token.riskLevel, t)})`).join(', ')}
            </TokenInfo>
          )}
        </ContentWrapper>
      </WarningContainer>
      {hasTaxToken && (
        <TaxNote>
          {inputIsTaxToken && (
            <>
              {t('sellToken')} {inputTokenAsToken?.symbol} {t('willChargeTax')} {inputTaxPercent}{t('percentTax')}，
            </>
          )}
          {outputIsTaxToken && (
            <>
              {t('buyToken')} {outputTokenAsToken?.symbol} {t('willChargeTax')} {outputTaxPercent}{t('percentTax')}，
            </>
          )}
          {t('taxAutoDeducted')}
        </TaxNote>
      )}
    </Container>
  )
}
